import React, { useState, useEffect, useRef, useCallback } from 'react';
import { companyAPI, authAPI } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { useAuth }    from '../../context/AuthContext';
import {
  BuildingOffice2Icon, MapPinIcon, BanknotesIcon,
  PhotoIcon, Cog6ToothIcon, UserCircleIcon,
  PlusIcon, TrashIcon, StarIcon, CheckCircleIcon, EyeIcon, EyeSlashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab',
  'Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
  'Uttarakhand','West Bengal','Delhi','Jammu and Kashmir','Ladakh',
  'Chandigarh','Puducherry',
];

const inp   = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white';
const lbl   = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50">
        <div className="w-8 h-8 bg-indigo-100 rounded-xl flex items-center justify-center">
          <Icon className="w-4 h-4 text-indigo-600" />
        </div>
        <h2 className="font-semibold text-gray-800 text-sm">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function ImageUpload({ label: lbl2, value, onChange, hint }) {
  const ref = useRef();
  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('File must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = ev => onChange(ev.target.result);
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <span className={lbl}>{lbl2}</span>
      <div className="flex items-start gap-4">
        <div onClick={() => ref.current.click()}
          className="w-28 h-28 border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors overflow-hidden bg-gray-50 flex-shrink-0">
          {value
            ? <img src={value} alt={lbl2} className="w-full h-full object-contain p-2" />
            : <><PhotoIcon className="w-8 h-8 text-gray-300 mb-1" /><span className="text-xs text-gray-400">Click to upload</span></>
          }
        </div>
        <div className="flex-1 space-y-2 pt-1">
          <button type="button" onClick={() => ref.current.click()}
            className="px-4 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-xl hover:bg-indigo-700 transition-colors">
            {value ? 'Change Image' : 'Upload Image'}
          </button>
          {value && (
            <button type="button" onClick={() => onChange(null)}
              className="block px-4 py-2 border border-red-200 text-red-600 text-xs font-semibold rounded-xl hover:bg-red-50 transition-colors">
              Remove
            </button>
          )}
          <p className="text-xs text-gray-400">{hint || 'PNG or JPG, max 2MB'}</p>
        </div>
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleFile} />
    </div>
  );
}

/* ─── Account / Password Tab ─────────────────────────────────── */
function AccountTab() {
  const { user, refreshUser } = useAuth();
  const [profile,  setProfile]  = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [showCurr, setShowCurr] = useState(false);
  const [showNew,  setShowNew]  = useState(false);
  const [savingP,  setSavingP]  = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async () => {
    if (!profile.name.trim()) { toast.error('Name is required'); return; }
    setSavingP(true);
    try {
      await authAPI.updateProfile({ name: profile.name, phone: profile.phone });
      await refreshUser();
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update profile'); }
    finally { setSavingP(false); }
  };

  const savePassword = async () => {
    if (!passwords.current) { toast.error('Enter current password'); return; }
    if (passwords.newPass.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (passwords.newPass !== passwords.confirm) { toast.error('Passwords do not match'); return; }
    setSavingPw(true);
    try {
      await authAPI.changePassword({ currentPassword: passwords.current, newPassword: passwords.newPass });
      toast.success('Password changed!');
      setPasswords({ current: '', newPass: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to change password'); }
    finally { setSavingPw(false); }
  };

  return (
    <div className="space-y-5">
      {/* Profile info */}
      <Section icon={UserCircleIcon} title="My Profile">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2 flex items-center gap-4 pb-4 border-b border-gray-100">
            <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <span className="text-2xl font-bold text-indigo-600">{(user?.name || '?')[0].toUpperCase()}</span>
            </div>
            <div>
              <p className="font-bold text-gray-900">{user?.name}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
              <span className="text-xs bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full capitalize">{user?.role}</span>
            </div>
          </div>
          <div>
            <label className={lbl}>Full Name</label>
            <input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
              placeholder="Your full name" className={inp} />
          </div>
          <div>
            <label className={lbl}>Phone Number</label>
            <input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
              placeholder="+91 98765 43210" className={inp} />
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Email Address</label>
            <input value={user?.email || ''} disabled
              className={inp + ' bg-gray-50 text-gray-400 cursor-not-allowed'} />
            <p className="text-xs text-gray-400 mt-1">Email cannot be changed. Contact admin if needed.</p>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button onClick={saveProfile} disabled={savingP}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors">
              {savingP ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</> : <><CheckCircleIcon className="w-4 h-4" />Save Profile</>}
            </button>
          </div>
        </div>
      </Section>

      {/* Change Password */}
      <Section icon={Cog6ToothIcon} title="Change Password">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-lg">
          <div className="md:col-span-2">
            <label className={lbl}>Current Password</label>
            <div className="relative">
              <input type={showCurr ? 'text' : 'password'} value={passwords.current}
                onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))}
                placeholder="Enter current password" className={inp + ' pr-10'} />
              <button type="button" onClick={() => setShowCurr(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showCurr ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className={lbl}>New Password</label>
            <div className="relative">
              <input type={showNew ? 'text' : 'password'} value={passwords.newPass}
                onChange={e => setPasswords(p => ({ ...p, newPass: e.target.value }))}
                placeholder="Min. 6 characters" className={inp + ' pr-10'} />
              <button type="button" onClick={() => setShowNew(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showNew ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className={lbl}>Confirm New Password</label>
            <input type="password" value={passwords.confirm}
              onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))}
              placeholder="Re-enter new password" className={inp} />
          </div>
          {passwords.newPass && passwords.confirm && passwords.newPass !== passwords.confirm && (
            <p className="md:col-span-2 text-xs text-red-500 -mt-2">Passwords do not match</p>
          )}
          <div className="md:col-span-2 flex justify-end">
            <button onClick={savePassword} disabled={savingPw}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors">
              {savingPw ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Changing...</> : <><CheckCircleIcon className="w-4 h-4" />Change Password</>}
            </button>
          </div>
        </div>
      </Section>
    </div>
  );
}

/* ─── Main Settings Page ─────────────────────────────────────── */
export function SettingsPage() {
  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [activeTab, setTab]     = useState('account');

  const [basic,    setBasic]    = useState({ name:'', tradeName:'', gstin:'', pan:'', cin:'', phone:'', altPhone:'', email:'', website:'' });
  const [address,  setAddress]  = useState({ street:'', city:'', state:'', pincode:'', country:'India' });
  const [banks,    setBanks]    = useState([]);
  const [logo,     setLogo]     = useState(null);
  const [signature,setSignature]= useState(null);
  const [prefs,    setPrefs]    = useState({ invoicePrefix:'INV', currencySymbol:'₹', invoiceTerms:'', invoiceFooter:'' });

  const load = useCallback(async () => {
    try {
      const res = await companyAPI.get();
      const p   = res.data || {};
      setProfile(p);
      setBasic({ name:p.name||'', tradeName:p.tradeName||'', gstin:p.gstin||'', pan:p.pan||'', cin:p.cin||'', phone:p.phone||'', altPhone:p.altPhone||'', email:p.email||'', website:p.website||'' });
      setAddress({ street:'', city:'', state:'', pincode:'', country:'India', ...(p.address||{}) });
      setBanks(p.bankAccounts || []);
      setLogo(p.logo || null);
      setSignature(p.signature || null);
      setPrefs({ invoicePrefix:p.invoicePrefix||'INV', currencySymbol:p.currencySymbol||'₹', invoiceTerms:p.invoiceTerms||'', invoiceFooter:p.invoiceFooter||'' });
    } catch { toast.error('Failed to load company profile'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!basic.name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      await companyAPI.update({ ...basic, address, bankAccounts: banks, logo, signature, ...prefs });
      toast.success('Company profile saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addBank = () => setBanks(b => [...b, { bankName:'', accountName:'', accountNumber:'', ifsc:'', branch:'', accountType:'current', upiId:'', isDefault: b.length === 0 }]);
  const updateBank = (i, field, val) => setBanks(b => b.map((bk, idx) => idx===i ? { ...bk, [field]: val } : bk));
  const removeBank = i => setBanks(b => b.filter((_,idx) => idx!==i));
  const setDefault = i => setBanks(b => b.map((bk,idx) => ({ ...bk, isDefault: idx===i })));

  const sb = f => ({ value: basic[f],   onChange: e => setBasic(v=>({...v,[f]:e.target.value})) });
  const sa = f => ({ value: address[f], onChange: e => setAddress(v=>({...v,[f]:e.target.value})) });
  const sp = f => ({ value: prefs[f],   onChange: e => setPrefs(v=>({...v,[f]:e.target.value})) });

  const TABS = [
    { key:'account',  label:'My Account',    Icon:UserCircleIcon },
    { key:'company',  label:'Company Info',  Icon:BuildingOffice2Icon },
    { key:'address',  label:'Address',       Icon:MapPinIcon },
    { key:'bank',     label:'Bank Details',  Icon:BanknotesIcon },
    { key:'branding', label:'Logo & Sign',   Icon:PhotoIcon },
    { key:'prefs',    label:'Preferences',   Icon:Cog6ToothIcon },
  ];

  const isCompanyTab = ['company','address','bank','branding','prefs'].includes(activeTab);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      <PageHeader title="Settings" subtitle="Manage your account, company profile, and preferences" />

      {/* Tab bar */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-1.5 flex gap-1 overflow-x-auto">
        {TABS.map(({ key, label: lbl2, Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap flex-1 justify-center
              ${activeTab===key ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'}`}>
            <Icon className="w-4 h-4" />{lbl2}
          </button>
        ))}
      </div>

      {/* Account tab */}
      {activeTab === 'account' && <AccountTab />}

      {/* Company Info tab */}
      {activeTab === 'company' && (
        <Section icon={BuildingOffice2Icon} title="Company Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className={lbl}>Company Name <span className="text-red-500">*</span></label>
              <input {...sb('name')} placeholder="Victor Radiant Industries Pvt Ltd" className={inp} />
            </div>
            <div>
              <label className={lbl}>Trade Name</label>
              <input {...sb('tradeName')} placeholder="VR Industries" className={inp} />
            </div>
            <div>
              <label className={lbl}>GSTIN</label>
              <input {...sb('gstin')} placeholder="22AAAAA0000A1Z5" maxLength={15} className={inp + ' font-mono uppercase'} />
            </div>
            <div>
              <label className={lbl}>PAN Number</label>
              <input {...sb('pan')} placeholder="ABCDE1234F" maxLength={10} className={inp + ' font-mono uppercase'} />
            </div>
            <div>
              <label className={lbl}>CIN Number</label>
              <input {...sb('cin')} placeholder="U12345GJ2020PTC123456" className={inp + ' font-mono uppercase'} />
            </div>
            <div className="md:col-span-2 border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">Contact Details</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div><label className={lbl}>Primary Phone</label><input {...sb('phone')} placeholder="+91 99255 22649" className={inp} /></div>
                <div><label className={lbl}>Alternate Phone</label><input {...sb('altPhone')} placeholder="+91 98765 43210" className={inp} /></div>
                <div><label className={lbl}>Email Address</label><input {...sb('email')} type="email" placeholder="info@vrindustriess.com" className={inp} /></div>
                <div><label className={lbl}>Website</label><input {...sb('website')} placeholder="www.vrindustriess.com" className={inp} /></div>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Address tab */}
      {activeTab === 'address' && (
        <Section icon={MapPinIcon} title="Registered Address">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className={lbl}>Street / Building</label>
              <textarea {...sa('street')} rows={2} placeholder="TF-321, Samanvay Business Square, Near Vaikunth Char Rasta" className={inp + ' resize-none'} />
            </div>
            <div><label className={lbl}>City</label><input {...sa('city')} placeholder="Vadodara" className={inp} /></div>
            <div>
              <label className={lbl}>State</label>
              <select {...sa('state')} className={inp}>
                <option value="">Select State</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Pincode</label><input {...sa('pincode')} placeholder="390019" maxLength={6} className={inp + ' font-mono'} /></div>
            <div><label className={lbl}>Country</label><input {...sa('country')} placeholder="India" className={inp} /></div>
          </div>
        </Section>
      )}

      {/* Bank Details tab */}
      {activeTab === 'bank' && (
        <Section icon={BanknotesIcon} title="Bank Accounts">
          <div className="space-y-4">
            {banks.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <BanknotesIcon className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                <p className="text-sm">No bank accounts added yet.</p>
              </div>
            )}
            {banks.map((bk, i) => (
              <div key={i} className={`border-2 rounded-2xl p-5 space-y-4 ${bk.isDefault ? 'border-indigo-300 bg-indigo-50/30' : 'border-gray-200 bg-gray-50/30'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 bg-indigo-100 text-indigo-600 text-xs font-bold rounded-full flex items-center justify-center">{i+1}</span>
                    <span className="text-sm font-semibold text-gray-700">{bk.bankName || 'New Bank Account'}</span>
                    {bk.isDefault && <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded-full font-medium">Default</span>}
                  </div>
                  <div className="flex gap-2">
                    {!bk.isDefault && (
                      <button type="button" onClick={() => setDefault(i)}
                        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors">
                        <StarIcon className="w-3.5 h-3.5" /> Set Default
                      </button>
                    )}
                    <button type="button" onClick={() => removeBank(i)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors">
                      <TrashIcon className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className={lbl}>Bank Name</label><input value={bk.bankName} onChange={e => updateBank(i,'bankName',e.target.value)} placeholder="HDFC Bank" className={inp} /></div>
                  <div><label className={lbl}>Account Holder Name</label><input value={bk.accountName} onChange={e => updateBank(i,'accountName',e.target.value)} placeholder="Victor Radiant Industries Pvt Ltd" className={inp} /></div>
                  <div><label className={lbl}>Account Number</label><input value={bk.accountNumber} onChange={e => updateBank(i,'accountNumber',e.target.value)} placeholder="50200120834401" className={inp + ' font-mono'} /></div>
                  <div><label className={lbl}>IFSC Code</label><input value={bk.ifsc} onChange={e => updateBank(i,'ifsc',e.target.value.toUpperCase())} placeholder="HDFC0001711" className={inp + ' font-mono uppercase'} /></div>
                  <div><label className={lbl}>Branch</label><input value={bk.branch} onChange={e => updateBank(i,'branch',e.target.value)} placeholder="Waghodiya Road, Vadodara" className={inp} /></div>
                  <div>
                    <label className={lbl}>Account Type</label>
                    <select value={bk.accountType} onChange={e => updateBank(i,'accountType',e.target.value)} className={inp}>
                      <option value="current">Current Account</option>
                      <option value="savings">Savings Account</option>
                      <option value="cc">Cash Credit</option>
                      <option value="od">Overdraft</option>
                    </select>
                  </div>
                  <div className="md:col-span-2"><label className={lbl}>UPI ID (optional)</label><input value={bk.upiId} onChange={e => updateBank(i,'upiId',e.target.value)} placeholder="vrindustries@hdfcbank" className={inp} /></div>
                </div>
              </div>
            ))}
            <button type="button" onClick={addBank}
              className="w-full py-3 border-2 border-dashed border-indigo-300 rounded-2xl text-indigo-600 text-sm font-semibold hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2">
              <PlusIcon className="w-4 h-4" /> Add Bank Account
            </button>
          </div>
        </Section>
      )}

      {/* Branding tab */}
      {activeTab === 'branding' && (
        <Section icon={PhotoIcon} title="Logo & Signature">
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <ImageUpload label="Company Logo" value={logo} onChange={setLogo} hint="PNG with transparent background recommended · Max 2MB" />
              <ImageUpload label="Authorised Signature / Stamp" value={signature} onChange={setSignature} hint="PNG with transparent background · Max 2MB · Used on invoices" />
            </div>
            {(logo || signature) && (
              <div className="border border-gray-200 rounded-2xl p-5 bg-gray-50">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">Preview on Invoice</p>
                <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {logo && <img src={logo} alt="Logo" className="h-12 object-contain" />}
                    <div>
                      <p className="font-bold text-gray-800 text-sm">{basic.name || 'Your Company Name'}</p>
                      <p className="text-xs text-gray-400">GSTIN: {basic.gstin || '24XXXXX'}</p>
                    </div>
                  </div>
                  {signature && (
                    <div className="text-center">
                      <img src={signature} alt="Signature" className="h-12 object-contain mb-1" />
                      <p className="text-xs text-gray-400">Authorised Signatory</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Preferences tab */}
      {activeTab === 'prefs' && (
        <Section icon={Cog6ToothIcon} title="Invoice Preferences">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={lbl}>Invoice Prefix</label>
              <input {...sp('invoicePrefix')} placeholder="INV" maxLength={5} className={inp + ' font-mono uppercase'} />
              <p className="text-xs text-gray-400 mt-1">Generated: <span className="font-mono text-indigo-600">{prefs.invoicePrefix || 'INV'}-2605-00001</span></p>
            </div>
            <div>
              <label className={lbl}>Currency Symbol</label>
              <select {...sp('currencySymbol')} className={inp}>
                <option value="₹">₹ — Indian Rupee (INR)</option>
                <option value="$">$ — US Dollar (USD)</option>
                <option value="€">€ — Euro (EUR)</option>
                <option value="£">£ — British Pound (GBP)</option>
                <option value="AED">AED — UAE Dirham</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className={lbl}>Default Terms & Conditions</label>
              <textarea {...sp('invoiceTerms')} rows={4} placeholder="1. Payment due within 30 days of invoice date." className={inp + ' resize-none'} />
              <p className="text-xs text-gray-400 mt-1">Appears in Notes section on every invoice by default.</p>
            </div>
            <div className="md:col-span-2">
              <label className={lbl}>Invoice Footer Note</label>
              <textarea {...sp('invoiceFooter')} rows={2} placeholder="Thank you for your business!" className={inp + ' resize-none'} />
            </div>
          </div>
        </Section>
      )}

      {/* Sticky Save Bar — only for company tabs */}
      {isCompanyTab && (
        <div className="sticky bottom-4 bg-white border border-gray-200 rounded-2xl shadow-lg px-6 py-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {profile?.updatedAt
              ? `Last saved: ${new Date(profile.updatedAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}`
              : 'Not saved yet'
            }
          </p>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm">
            {saving
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</>
              : <><CheckCircleIcon className="w-4 h-4" />Save All Changes</>
            }
          </button>
        </div>
      )}
    </div>
  );
}